/**
 * Geocode a zip code to coordinates using Google Maps Geocoding API
 * Returns { lat, lng } or null if geocoding fails
 */
export async function geocodeZipCode(zipCode: string): Promise<{ lat: number; lng: number } | null> {
  const apiKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY
  
  if (!apiKey) {
    console.error('[Geocode] Google Maps API key not found')
    return null
  }

  try {
    // Geocode the zip code
    const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(zipCode)}&key=${apiKey}`
    const response = await fetch(url)
    const data = await response.json()

    if (data.status === 'OK' && data.results && data.results.length > 0) {
      const location = data.results[0].geometry.location
      return {
        lat: location.lat,
        lng: location.lng
      }
    } else {
      console.warn(`[Geocode] Failed to geocode zip code ${zipCode}:`, data.status)
      return null
    }
  } catch (error) {
    console.error(`[Geocode] Error geocoding zip code ${zipCode}:`, error)
    return null
  }
}

/**
 * Batch geocode multiple zip codes
 * Returns a map of zipCode -> { lat, lng }
 */
export async function geocodeZipCodes(zipCodes: string[]): Promise<Map<string, { lat: number; lng: number }>> {
  const results = new Map<string, { lat: number; lng: number }>()
  
  // Geocode all zip codes in parallel
  const promises = zipCodes.map(async (zipCode) => {
    const coords = await geocodeZipCode(zipCode)
    if (coords) {
      results.set(zipCode, coords)
    }
  })

  await Promise.all(promises)
  return results
}

/**
 * Geocode a full address to coordinates using Google Maps Geocoding API
 * Returns { lat, lng } or null if geocoding fails
 */
export async function geocodeAddress(address: string): Promise<{ lat: number; lng: number } | null> {
  const apiKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY
  
  if (!apiKey) {
    console.error('[Geocode] Google Maps API key not found')
    return null
  }

  try {
    // Geocode the full address
    const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${apiKey}`
    const response = await fetch(url)
    const data = await response.json()

    if (data.status === 'OK' && data.results && data.results.length > 0) {
      const location = data.results[0].geometry.location
      return {
        lat: location.lat,
        lng: location.lng
      }
    } else {
      console.warn(`[Geocode] Failed to geocode address ${address}:`, data.status)
      return null
    }
  } catch (error) {
    console.error(`[Geocode] Error geocoding address ${address}:`, error)
    return null
  }
}

/**
 * Extract zip code from an address string
 * Looks for 5-digit zip codes (optionally followed by -4 digits)
 * Returns the zip code string or null if not found
 */
export function extractZipCodeFromAddress(address: string): string | null {
  if (!address) return null
  
  // Match 5-digit zip code (optionally followed by -4 digits)
  // Common patterns: "75201", "75201-1234", "TX 75201", "Austin, TX 75201"
  const zipCodePattern = /\b(\d{5}(?:-\d{4})?)\b/
  const match = address.match(zipCodePattern)
  
  if (match && match[1]) {
    return match[1].split('-')[0] // Return just the 5-digit zip code
  }
  
  return null
}

